import React from 'react';
import { Box } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { SonosSpeaker } from '../../services/sonosApi';
import {
  sonosCardOuterSx,
  SonosCardIcon,
  SonosCardPlaybackBody,
  SonosCardAiBadge,
} from './sonosCardLayout';

interface SonosSpeakerCardProps {
  speaker: SonosSpeaker;
  canControl?: boolean;
  onOpenModal?: (speaker: SonosSpeaker) => void;
  onTransport?: (speakerId: number, action: 'play' | 'pause' | 'previous' | 'next') => void;
  isRoomEditing?: boolean;
  dragHandleProps?: Record<string, unknown>;
}

/** Misma idea que DeviceCard: overlay pan-y para no bloquear scroll en móvil (docs/51). */
const SonosSpeakerCard: React.FC<SonosSpeakerCardProps> = ({
  speaker,
  canControl = false,
  onOpenModal,
  onTransport,
  isRoomEditing = false,
  dragHandleProps,
}) => {
  const readOnlyOpacity = canControl ? 1 : 0.45;

  const handleTransport = (speakerId: number, action: 'play' | 'pause') => {
    onTransport?.(speakerId, action);
  };

  return (
    <Box
      sx={{
        ...sonosCardOuterSx,
        opacity: speaker.online ? readOnlyOpacity : 0.5,
        pointerEvents: 'none',
        '& button, & .MuiIconButton-root, & .MuiButton-root, & [role="button"], & [data-device-icon], & [data-audio-player-control]': {
          pointerEvents: 'auto',
          touchAction: 'manipulation',
        },
      }}
    >
      <Box
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('button') || target.closest('.MuiIconButton-root') || target.closest('[data-device-icon]') || target.closest('[data-audio-player-control]')) {
            return;
          }
          const scrolled = (window as any).dashboardHasScrolledRef?.current;
          if (!scrolled) onOpenModal?.(speaker);
        }}
        sx={{
          position: 'absolute',
          top: 0,
          left: '80px',
          /* Hueco tapa grande + play a la derecha */
          right: '140px',
          bottom: 0,
          pointerEvents: 'auto',
          touchAction: 'pan-y',
          zIndex: 1,
          cursor: 'pointer',
        }}
      />
      <SonosCardAiBadge speaker={speaker} />
      <SonosCardIcon speaker={speaker} />
      <SonosCardPlaybackBody
        speaker={speaker}
        canControl={canControl}
        onTransport={handleTransport}
        showControl={!isRoomEditing}
      />
      {isRoomEditing && (
        <Box
          {...dragHandleProps}
          sx={{
            marginLeft: 0.5,
            transform: 'translateX(-10px)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            cursor: 'grab',
            pointerEvents: 'auto',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicator sx={{ color: 'white', fontSize: '2.25rem', pointerEvents: 'none' }} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(SonosSpeakerCard, (prev, next) => (
  prev.speaker.id === next.speaker.id
  && prev.speaker.playback_state === next.speaker.playback_state
  && prev.speaker.current_title === next.speaker.current_title
  && prev.speaker.current_artist === next.speaker.current_artist
  && prev.speaker.current_album === next.speaker.current_album
  && prev.speaker.current_album_art_url === next.speaker.current_album_art_url
  && prev.speaker.track_position === next.speaker.track_position
  && prev.speaker.track_duration === next.speaker.track_duration
  && prev.speaker.volume === next.speaker.volume
  && prev.speaker.online === next.speaker.online
  && prev.speaker.group_coordinator_uid === next.speaker.group_coordinator_uid
  && prev.speaker.is_coordinator === next.speaker.is_coordinator
  && prev.speaker.playing_source_name === next.speaker.playing_source_name
  && prev.speaker.pin_required === next.speaker.pin_required
  && prev.speaker.artefacto_id === next.speaker.artefacto_id
  && prev.speaker.artefacto?.nombre === next.speaker.artefacto?.nombre
  && prev.canControl === next.canControl
  && prev.isRoomEditing === next.isRoomEditing
));
